import cx from 'classnames'
import React, { useEffect } from 'react'

import { activateNextPane, togglePlay } from '../../actions/play-actions'
import spinner from '../../assets/img/spinner.gif'
import {
  COUNTDOWN_CLOCK_SIZE,
  LYRIC_TEXT_COLORS,
  Pane,
  PaneClasses,
  VIZ_CONTAINER_CSS_PREFIX,
} from '../../constants/constants'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { selectIsFocus } from '../../reducers/focusSlice'
import {
  selectCurrentLyricsColorIndex,
  selectMode,
  selectPrefs,
  selectVisualizationInfo,
} from '../../selectors/session-selectors'
import { selectCurrentGamers, selectIsPlaying } from '../../selectors/current-play-selectors'
import trackMixer from '../../services/TrackMixer'
import { LoadingPhases, LoadStatus } from '../../types'
import LyricEditModal from '../modals/LyricEditModal'
import themes from '../themes'
import CountdownClock from '../widgets/CountdownClock'

const LOAD_PHASES = {
  clearing: 'clearing previous',
  info: 'info',
  track: 'track resources',
  text: 'text',
  timing: 'timing',
  audio: 'audio',
  image: 'image',
  processing: 'preparing',
}

const InteractionView = () => {
  const dispatch = useAppDispatch()
  const isCurrFocus = useAppSelector(selectIsFocus(Pane.PLAY))
  const mode = useAppSelector(selectMode)
  const lyricEditRange = useAppSelector((state) => state.modals.lyricEditRange)
  const trackLoadStatus = useAppSelector((state) => state.loading || {})
  const trackError = useAppSelector((state) => state.session.trackError)
  const visualizationInfo = useAppSelector(selectVisualizationInfo)
  const isShowCountdownClock = useAppSelector((state) => state.session.isShowCountdownClock)
  const countdownSeconds = useAppSelector((state) => state.session.countdownSeconds)
  const isPlaying = useAppSelector(selectIsPlaying)
  const { theme: currentThemeIndex } = useAppSelector(selectPrefs)
  const lyricTextColorIndex = useAppSelector(selectCurrentLyricsColorIndex)
  const gamers = useAppSelector(selectCurrentGamers)
  const isLoading = LoadingPhases.some((key) => {
    const status = trackLoadStatus[key]
    return status === LoadStatus.LOADING || status === LoadStatus.ERROR
  })
  useEffect(() => {
    if (trackMixer.isLoading && !isLoading) {
      dispatch(togglePlay(false))
    }
  })
  const displayLoadingStatus = () => {
    return (
      <div className="loadingContainer">
        {LoadingPhases.map((phase) => {
          const status = trackLoadStatus[phase]
          if (status === LoadStatus.IDLE) {
            return null
          }
          const phaseName = LOAD_PHASES[phase]
          const isSuccess = status === LoadStatus.SUCCESS
          const isError = status === LoadStatus.ERROR
          const classname = cx({
            loadingStatus: true,
            isLoading: !isSuccess,
          })
          const message = isSuccess ? 'success' : isError ? 'error' : '...'
          return (
            <div id={phase} key={phase} className="loadingStatus">
              {phaseName}
              <span className={classname}>{message}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const onClick = () => {
    // console.log(`container: ${PaneClasses[Pane.PLAY]}`)
    dispatch(activateNextPane({ pane: Pane.PLAY }))
  }
  const wrapperClassname = cx(PaneClasses[Pane.PLAY], {
    hasFocus: isCurrFocus,
    isPlaying,
    [`${mode}Mode`]: true,
  })
  const _getCountdownClock = () => {
    const color = themes[currentThemeIndex].cssVars['color-txt-title']
    if (isShowCountdownClock && countdownSeconds) {
      return (
        <CountdownClock
          seconds={countdownSeconds}
          color={color}
          alpha={0.6}
          size={COUNTDOWN_CLOCK_SIZE}
          onComplete={() => trackMixer.start()}
        />
      )
    }
  }
  const activePlayers = gamers.filter(({ isActive }, index) => isActive || index === 0)
  return (
    <div className={wrapperClassname} onClick={onClick}>
      {activePlayers.map(({ isActive }, index) => {
        const containerClassname = cx({
          interactionContainer: true,
          [`${VIZ_CONTAINER_CSS_PREFIX}${visualizationInfo[index].currentVizSlug}`]: true,
          hidden: !isActive || isLoading || trackError || lyricEditRange,
          notFirst: index > 0,
        })
        return (
          <div
            key={`${index}`}
            id={`gameContainer-${index}`}
            className={containerClassname}
            style={{ color: LYRIC_TEXT_COLORS[lyricTextColorIndex].color }}
          ></div>
        )
      })}
      {_getCountdownClock()}
      {lyricEditRange && <LyricEditModal selectedRange={lyricEditRange} />}
      {isLoading && (
        <div className="interactionContainer">
          <div className="newLoadingIndicator">
            <img className="spinner" src={spinner} alt="spinner" />
            {displayLoadingStatus()}
          </div>
        </div>
      )}
    </div>
  )
}

export default InteractionView
